import { getComparisons, updateCompare } from '../actions/compare-server-actions'
import {
  ComparisonsFragment,
  GetComparisonsQuery,
} from '../graphql/product/generated/graphql-types'

import { use, useCallback } from 'react'
import { createProductListItem, createProductList } from '../utils/create-product-list'
import { useSWR } from './use-graphql-swr'
import { getCacheKeyFromDocument } from './graphql-fetcher'
import { GET_COMPARISONS } from '../graphql/product/get-customer-product-lists.graphql'
import { Middleware, SWRHook, useSWRConfig } from 'swr'

export type ProductListWithoutProduct =
  | Omit<ComparisonsFragment['productLists'], 'product'>
  | undefined
export function isItemInList(id?: string | number, data?: GetComparisonsQuery): boolean {
  if (!data || !data.comparisons || !data.comparisons.productLists) {
    return false
  }
  return data.comparisons.productLists.some(productList => {
    return productList.items.some(item => item.productErpIdentifier === id)
  })
}
export type UseComparisonsReturn = typeof useComparisonsSwr


const resolveReactPromise: Middleware = (useSWRNext: SWRHook) => (key, fetcher, config) => {
  const { fallback } = useSWRConfig()
  const cacheKey = getCacheKeyFromDocument(GET_COMPARISONS)
  const cachedData = fallback[cacheKey] as
    | GetComparisonsQuery
    | Promise<GetComparisonsQuery>
    | undefined
  if (typeof cachedData === 'object' && cachedData instanceof Promise) {
    const data = use(cachedData)
    return useSWRNext(key, fetcher, { ...config, fallbackData: data as any })
  }
  return useSWRNext(key, fetcher, config)
}

export default function useComparisonsSwr() {
  const {
    data: comparisonsData,
    error,
    isValidating: loading,
    mutate,
  } = useSWR<GetComparisonsQuery>(getCacheKeyFromDocument(GET_COMPARISONS), getComparisons, {
    use: [resolveReactPromise],
  })

  // get data
  const updateComparisons = useCallback(
    (
      comparisonData: ProductListWithoutProduct,
      productId: string,
      categoryName: string,
      displayName: string
    ) => {
      const comparison = comparisonData?.find(item =>
        item?.items?.some(i => i?.productErpIdentifier === productId)
      )

      const itemExist = !!comparison

      return mutate(
        async () => {
          const newData = await updateCompare(
            productId,
            comparison?.productListName,
            itemExist ? 0 : 1
          )

          const oldProductList = comparisonsData?.comparisons?.productLists
          const productListId = newData?.updateCompareListWithProduct?.productListId
          const productListItemId = newData?.updateCompareListWithProduct?.productListItemId

          let updatedProductList

          if (itemExist) {
            // Remove
            updatedProductList = removeOnUpdate(comparisonData, oldProductList, { productId })
          } else {
            // Add
            updatedProductList = addOnUpdate(oldProductList, {
              productListId,
              categoryName,
              productListItemId,
              productId,
              displayName,
            })
          }

          const updatedData = {
            comparisons: { __typename: 'Comparisons', productLists: updatedProductList },
          }

          return updatedData as GetComparisonsQuery
        },
        {
          revalidate: false,
          rollbackOnError: true,
        }
      )
    },
    [comparisonsData?.comparisons?.productLists, mutate]
  )

  if (error) {
    throw error
  }
  return {
    error,
    updateComparisons,
    comparisonsData: comparisonsData?.comparisons?.productLists ?? [],
    loading,
    isItemInList: (id: string | number) => isItemInList(id, comparisonsData),
  }
}

function addOnUpdate(
  oldProductList: ProductListWithoutProduct,
  createParams: {
    categoryName: string
    productId: string
    displayName: string
    productListId: number | undefined
    productListItemId: number | undefined
  }
) {
  const category = oldProductList?.find(
    listItem => listItem?.productListName === createParams.categoryName
  )

  const newProductListItem = createProductListItem(
    createParams.productListItemId,
    createParams.productId,
    createParams.displayName
  )

  if (category) {
    const updatedCategory = {
      ...category,
      items: category.items ? [...category.items, newProductListItem] : [newProductListItem],
    }

    return oldProductList
      ? [
          ...oldProductList.filter(
            list => list?.productListName !== updatedCategory.productListName
          ),
          updatedCategory,
        ]
      : [updatedCategory]
  } else {
    // Add new category
    const newProductList = createProductList(
      createParams.productListId,
      createParams.categoryName,
      createParams.productListItemId,
      createParams.productId,
      createParams.displayName
    )

    return oldProductList ? [...oldProductList, newProductList] : [newProductList]
  }
}

function removeOnUpdate(
  list: ProductListWithoutProduct,
  oldProductList: ProductListWithoutProduct,
  params: {
    productId: string
  }
) {
  const currentProductList = list?.find(listItem =>
    listItem?.items?.some(productItem => productItem?.productErpIdentifier === params.productId)
  )

  const currentProductListItem = currentProductList?.items?.find(productItem => {
    return productItem?.productErpIdentifier === params.productId
  })

  return oldProductList
    ?.map(listItem => {
      const filteredItems = listItem?.items?.filter(item => item?.id !== currentProductListItem?.id)
      return { ...listItem, items: filteredItems }
    })
    .filter(item => item.items?.length)
}
